import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";

export interface FieldChange {
  label: string;
  from: string;
  to: string;
}

export interface ObjectChange {
  kind: "ADD" | "UPDATE" | "DELETE";
  fieldType: string;
  fieldValue: string;
}

interface Props {
  open: boolean;
  /** "Config" / "Report" / "Interface" */
  entityLabel: string;
  itemName: string;
  fieldChanges: FieldChange[];
  objectChanges: ObjectChange[];
  /** how many brands this change reaches on the next run */
  affectedCount: number;
  affectedDescription: string;
  /** one line per dependant, e.g. a config name and its brand count */
  dependants: { primary: string; secondary?: string }[];
  onClose: () => void;
  onConfirm: () => void;
}

const STEPS = ["Summary", "Impact"];

const CHANGE_COLOR: {
  [key in ObjectChange["kind"]]: "success" | "info" | "error";
} = {
  ADD: "success",
  UPDATE: "info",
  DELETE: "error",
};

/**
 * The commit step shared by the config, report and interface editors: what
 * changed, then who it reaches, then save. Nothing is written until the last
 * button - object deletes travel with the parent's own call.
 */
const SaveChangesReviewModal = ({
  open,
  entityLabel,
  itemName,
  fieldChanges,
  objectChanges,
  affectedCount,
  affectedDescription,
  dependants,
  onClose,
  onConfirm,
}: Props) => {
  const [step, setStep] = useState(0);

  const close = () => {
    setStep(0);
    onClose();
  };

  const counts = {
    ADD: objectChanges.filter((change) => change.kind === "ADD").length,
    UPDATE: objectChanges.filter((change) => change.kind === "UPDATE").length,
    DELETE: objectChanges.filter((change) => change.kind === "DELETE").length,
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      PaperProps={{ sx: { width: "660px", maxWidth: "94vw" } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        {step === 0 ? `Changes to ${entityLabel}` : "Who this affects"}
      </DialogTitle>
      <Typography variant="body2" sx={{ px: 3, pb: 1.5, color: "#00000099" }}>
        {step === 0 ? itemName : "Changes apply on the next scheduled run."}
      </Typography>
      <Divider />
      <DialogContent sx={{ px: 3, py: 2 }}>
        <Stepper activeStep={step} sx={{ mb: 2.5 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 0 ? (
          <>
            <Typography
              sx={{
                fontSize: "11px",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                color: "#00000099",
                fontWeight: 700,
                mb: 1,
              }}
            >
              Fields
            </Typography>
            {fieldChanges.length === 0 ? (
              <Typography sx={{ fontSize: "13px", color: "#00000099" }}>
                No field changed.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {fieldChanges.map((change) => (
                  <Stack
                    key={change.label}
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                  >
                    <Typography
                      sx={{ fontSize: "13px", width: 170, flex: "none" }}
                    >
                      {change.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "#00000099",
                        textDecoration: "line-through",
                      }}
                    >
                      {change.from || "-"}
                    </Typography>
                    <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                      → {change.to || "-"}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}

            {objectChanges.length > 0 && (
              <Box sx={{ mt: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    color: "#00000099",
                    fontWeight: 700,
                    mb: 1,
                  }}
                >
                  Objects · {counts.ADD} add · {counts.UPDATE} update ·{" "}
                  {counts.DELETE} delete
                </Typography>
                <Stack spacing={1}>
                  {objectChanges.map((change, index) => (
                    <Stack
                      key={`${change.kind}-${change.fieldType}-${index}`}
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                    >
                      <Chip
                        size="small"
                        label={change.kind}
                        color={CHANGE_COLOR[change.kind]}
                        sx={{ width: 84, flex: "none" }}
                      />
                      <Typography
                        sx={{ fontSize: "12.5px", fontWeight: 500, width: 170 }}
                      >
                        {change.fieldType}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "12px",
                          color: "#00000099",
                          wordBreak: "break-all",
                        }}
                      >
                        {change.fieldValue.slice(0, 90)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
                <Typography
                  sx={{ fontSize: "12px", color: "#00000099", mt: 1 }}
                >
                  Deletes travel as one objects.delete[] id list in the same
                  call.
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <>
            {affectedCount > 0 ? (
              <Alert severity="warning">
                <b>{affectedCount}</b> {affectedDescription}.
              </Alert>
            ) : (
              <Alert severity="info">
                Nothing is connected. This change is safe.
              </Alert>
            )}
            <Stack spacing={1} sx={{ mt: 2 }}>
              {dependants.length === 0 ? (
                <Typography sx={{ fontSize: "13px", color: "#00000099" }}>
                  No dependants.
                </Typography>
              ) : (
                dependants.slice(0, 12).map((row) => (
                  <Stack
                    key={row.primary}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{
                      border: "1px solid #0000001F",
                      borderRadius: "7px",
                      px: 1.5,
                      py: 1,
                    }}
                  >
                    <Typography sx={{ fontSize: "13px", flex: 1 }}>
                      {row.primary}
                    </Typography>
                    {row.secondary && (
                      <Typography
                        sx={{ fontSize: "12px", color: "#00000099" }}
                      >
                        {row.secondary}
                      </Typography>
                    )}
                  </Stack>
                ))
              )}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step > 0 && (
          <Button color="secondary" onClick={() => setStep(step - 1)}>
            Back
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button color="secondary" onClick={close}>
          Cancel
        </Button>
        {step === 0 ? (
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setStep(1)}
          >
            Review impact
          </Button>
        ) : (
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              setStep(0);
              onConfirm();
            }}
          >
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SaveChangesReviewModal;
